import { PropTypes } from 'prop-types';

import { Link } from 'react-router-dom';

import styled from '@emotion/styled';

import Spacer from 'Components/Spacer';
import Typography from 'Components/Typography';

import { srcOrNextImageProp } from 'PropTypes/imageProps';

import arrow from 'assets/images/skincare/link-arrow.svg';

import useResponsiveVariant from 'utils/useResponsiveVariant';

const Root = styled.div`
  width: 100%;
  background-color: ${props => props.theme.palette.common.beige.light};
  overflow: hidden;
`;

const Container = styled.div`
  width: 100%;
  padding: 92px 24px;

  ${props => props.theme.breakpoints.up('md')} {
    padding: 150px 80px;
  }
`;

const StyledTitle = styled(Typography)`
  ${props => props.theme.breakpoints.match('sm')} {
    font-size: 16px;
    margin-bottom: 8px;
  }
`;
const StyledPrimaryTitle = styled(Typography)`
  ${props => props.theme.breakpoints.match('sm')} {
    font-size: 36px;
  }
`;

const ImagesContainer = styled.div`
  width: 100%;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-template-rows: 1fr 1fr;
  grid-row-gap: 36px;
  grid-column-gap: 24px;
  ${props => props.theme.breakpoints.up('sm')} {
    grid-template-columns: repeat(4, 1fr);
    grid-template-rows: 1fr;
  }
`;

const ImageContainer = styled.div`
  position: relative;
`;

const Image = styled('img')`
  width: 100%;
`;

const Icon = styled('img')`
  margin-left: 16px;
`;

const StyledLink = styled(Link)`
  &:hover {
    text-decoration: underline;
    color: inherit;
  }
`;

const LinkContainer = styled('div')`
  display: flex;
  &:hover {
    text-decoration: underline;
    color: inherit;
  }
`;

const NewTag = styled(Typography)`
  position: absolute;
  color: black;
  bottom: 16px;
  left: 8px;
  background-color: ${props => props.theme.palette.common.white.light};
  padding: 4px 18px;
`;

const CategoryCard = ({ imageData }) => {
  const { isMobile } = useResponsiveVariant();

  const { desktop, mobile, category, path, isNew, dataFrom, dataClick } = imageData;
  return (
    <div>
      <StyledLink data-click={dataClick} data-from={dataFrom} to={path}>
        <ImageContainer>
          <Image alt="category-image" src={isMobile ? mobile.src : desktop.src} />
          {isNew && (
            <NewTag italic variant="p3">
              New
            </NewTag>
          )}
        </ImageContainer>
        <LinkContainer>
          <Typography color="noir" medium upperCase variant="p1">
            {category}
          </Typography>
          <Icon alt="arrow-right" src={arrow} />
        </LinkContainer>
      </StyledLink>
    </div>
  );
};

CategoryCard.propTypes = {
  imageData: PropTypes.shape({
    desktop: srcOrNextImageProp,
    mobile: srcOrNextImageProp,
    path: PropTypes.string,
    dataFrom: PropTypes.string,
    dataClick: PropTypes.string,
    category: PropTypes.string,
    isNew: PropTypes.bool,
  }).isRequired,
};

const ExploreMore = ({ content }) => {
  const { primaryTitle, title, images } = content;
  return (
    <Root>
      <Container>
        <StyledTitle align="center" markupName="h2" variant="mono2">
          {title}
        </StyledTitle>
        <StyledPrimaryTitle align="center" variant="hero2">
          {primaryTitle}
        </StyledPrimaryTitle>
        <Spacer size={42} />
        <ImagesContainer>
          {images.map((imageData, i) => {
            return <CategoryCard key={String(i)} imageData={imageData} />;
          })}
        </ImagesContainer>
      </Container>
    </Root>
  );
};

ExploreMore.propTypes = {
  content: PropTypes.shape({
    primaryTitle: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    images: PropTypes.arrayOf(
      PropTypes.shape({
        desktop: srcOrNextImageProp,
        mobile: srcOrNextImageProp,
        path: PropTypes.string,
        category: PropTypes.string,
        isNew: PropTypes.bool,
      })
    ),
  }).isRequired,
};

export default ExploreMore;
